<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import cloneDeep from 'lodash/cloneDeep';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const headers = ref(['', t('quote_config.pax'), t('quote_config.rooms_cabins')]);
const adminSetupStore = useAdminSetupStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const minimumGroupSize = ref({});

const getTitleFromConfigKey = (key: string): string => {
  switch (key) {
    case 'hotelConfig':
      return t('management.email.hotel_quote');

    case 'airConfig':
      return t('management.email.air_quote');

    case 'cruiseConfig':
      return t('management.email.cruise_quote');

    case 'tourConfig':
      return t('advance_quote.form.multi_day_tour');

    default:
      return t('management.email.package_quote');
  }
};

const isHideConfig = computed(() => {
  return authorizations?.configurationPage?.configurable_items_edit;
});

const filteredConfig = computed(() => Object.entries(minimumGroupSize.value).filter(([key]) => key !== 'pk' && key !== 'sk'));

const getMinimumConfig = () => {
  adminSetupStore
    .getMinimumConfig()
    .then((res: any) => {
      minimumGroupSize.value = cloneDeep(res);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const handleSave = (groupSize: any, key: string) => {
  const payload: any = {
    pk: minimumGroupSize.value?.pk,
    sk: minimumGroupSize.value?.sk,
    [key]: groupSize
  };
  adminSetupStore
    .updateMinimumConfig(payload)
    .then(() => {
      getMinimumConfig();
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

onMounted(() => {
  getMinimumConfig();
});
</script>
<template>
  <div class="card minimum-pax">
    <h4>{{ $t('quote_config.min_max_config') }}</h4>
    <div class="flex flex-wrap justify-content-between mt-5">
      <div>
        <h5 class="title">{{ $t('quote_config.minimum_group_size') }}</h5>
      </div>
    </div>
    <div>
      <div class="flex mb-2">
        <div :class="{ 'w-20rem': !header, 'col-1': header }" class="ml-1 flex justify-content-center" v-for="header in headers" :key="header">
          <b>{{ header }}</b>
        </div>
      </div>
      <div class="flex mb-3 input-number-field" v-for="([key, groupSize], index) in filteredConfig" :key="index">
        <div class="align-self-center mr-3 w-20rem justify-content-center">
          {{ getTitleFromConfigKey(key) }}
        </div>
        <div class="col-1 flex justify-content-center">
          <InputNumber class="mr-3 pax-number" v-model="groupSize.minPax"></InputNumber>
        </div>
        <div class="col-1 flex justify-content-center">
          <InputNumber v-if="key !== 'airConfig'" class="mr-3 room-number" v-model="groupSize.minRoomsCabins"></InputNumber>
        </div>
        <div class="col-1 ml-4 flex justify-content-center">
          <Button v-if="isHideConfig" class="mr-2 ml-3 bg-primary save-btn" :label="$t('promos.save')" icon="pi pi-check save-btn" @click="handleSave(groupSize, key)"></Button>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.minimum-pax {
  .p-inputnumber-input {
    width: 4rem !important;
  }
}
</style>
